/* global GeolocationPositionError */
import { InputBlock } from '@coast/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { GeolocationSnapshot } from '@shared/components/blocks/types';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, getAnchorName } from '@shared/styles';
import { useCurrentUserId } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { renderLabel as renderDefaultLabel } from '../input';
import { MBCardInputBlockBase } from '../input/base';
import { GeolocationContent } from './content';
import { PromptBottomSheetContent } from './promptBottomSheetContent';
import { useFetchCurrentGeolocation } from './utils';

interface Props {
  block: InputBlock;
}

export function MBCardInputBlockGeolocation({ block }: Props) {
  const colors = useContext(ColorThemeContext);
  const currentUserId = useCurrentUserId();
  const anchorName = getAnchorName('input-geolocation', block.name);

  const { setFieldValue, setFieldError, values } = useCardFormContext();
  const geolocation = values[block.name] as GeolocationSnapshot;
  const TypoComponent = getTypoComponent(block.textStyle);

  const [showingPrompt, setShowingPromt] = useState(false);
  const showPrompt = useCallback(() => setShowingPromt(true), []);
  const hidePrompt = useCallback(() => setShowingPromt(false), []);

  const clearValue = useCallback(
    () => setFieldValue(block.name, undefined),
    [setFieldValue, block.name],
  );
  const fetchCurrentGeolocation = useFetchCurrentGeolocation();
  const handleFetchLocation = useCallback(async () => {
    hidePrompt();
    try {
      clearValue();
      const coords = await fetchCurrentGeolocation();
      const updatedGeolocation: GeolocationSnapshot = {
        // @ts-expect-error -- strict migration
        userId: currentUserId,
        date: new Date().toISOString(),
        accuracy: coords.accuracy,
        coords: {
          lat: coords.latitude,
          lon: coords.longitude,
        },
      };
      setFieldValue(block.name, updatedGeolocation);
    } catch (_error) {
      const error = _error as GeolocationPositionError;
      // GeolocationPositionError.PERMISSION_DENIED = 1
      if (error.code === 1) {
        setFieldError(block.name, i18n('card.blocks.geolocationInput.permissionDeniedError.web'));
      } else {
        setFieldError(block.name, error.message);
      }
    }
  }, [
    hidePrompt,
    setFieldValue,
    setFieldError,
    currentUserId,
    block.name,
    fetchCurrentGeolocation,
    clearValue,
  ]);

  const empty = !geolocation;
  const renderLabel = useMemo(() => (!empty ? renderDefaultLabel : undefined), [empty]);

  return (
    <>
      <MBTouchableOpacity disabled={!empty} onPress={showPrompt}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderLabel={renderLabel}>
            {empty ? (
              <TypoComponent color={colors.foregroundInactive}>{block.placeholder}</TypoComponent>
            ) : (
              <GeolocationContent geolocation={geolocation} onClear={clearValue} />
            )}
          </MBCardInputBlockBase>
        </View>
      </MBTouchableOpacity>
      {showingPrompt && (
        <PromptBottomSheetContent
          anchorName={anchorName}
          onPress={handleFetchLocation}
          onClose={hidePrompt}
        />
      )}
    </>
  );
}
